"use client";

import { Time } from "@commerce/components/time";
import { STAFF_MESSAGE_MAX } from "@repo/api/modules/channels/types";
import { useSession } from "@saas/auth/client";
import { useActiveOrganization } from "@saas/organizations/client";
import { errorMessage } from "@saas/settings/lib/errors";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { UserAvatar } from "@shared/components/UserAvatar";
import { orpc } from "@shared/lib/orpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { DisabledReason, InfoTip } from "@ui/components/info-tip";
import { Kbd, KbdGroup } from "@ui/components/kbd";
import { Spinner } from "@ui/components/spinner";
import { Textarea } from "@ui/components/textarea";
import { toast } from "@ui/components/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	BotIcon,
	ChevronDownIcon,
	HeadsetIcon,
	SendIcon,
	UserRoundIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { ConversationDetail } from "./types";
import { SendMenu, SendTemplateButton } from "./whatsapp/SendMenu";

/**
 * Handoff in the inbox (ADR-9): who holds a conversation, taking it over from the agent and
 * handing it back, assigning it, and writing to the shopper while the agent is paused.
 */

interface Person {
	id: string;
	name: string;
	image?: string | null;
}

const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/** The row badge: asked for a person, or held by one. */
export function HandoffBadge({
	requested,
	paused,
	assignee,
}: {
	requested: boolean;
	paused: boolean;
	assignee: Person | null;
}) {
	if (paused) {
		return (
			<Badge variant="info">
				<UserRoundIcon aria-hidden />
				{assignee
					? `With ${firstName(assignee.name)}`
					: "With your team"}
			</Badge>
		);
	}
	return requested ? (
		<Badge variant="warning">
			<HeadsetIcon aria-hidden />
			Needs a person
		</Badge>
	) : null;
}

/**
 * The open conversation's cache for the handoff actions: changed at once, put back when the
 * server refuses, and every conversation query refetched after.
 */
function useHandoffCache(detail: ConversationDetail) {
	const queryClient = useQueryClient();
	const key = orpc.conversations.get.queryKey({
		input: { conversationId: detail.id },
	});
	const patch = (change: Partial<ConversationDetail>) =>
		queryClient.setQueryData<ConversationDetail>(key, (old) =>
			old ? { ...old, ...change } : old,
		);
	const settle = () =>
		queryClient.invalidateQueries(
			{ queryKey: orpc.conversations.key() },
			{ cancelRefetch: false },
		);
	const failed = (title: string) => (error: unknown) => {
		patch({
			agentPausedAt: detail.agentPausedAt,
			assignee: detail.assignee,
			handoffRequestedAt: detail.handoffRequestedAt,
		});
		toast.add({
			type: "error",
			title,
			description: errorMessage(error, "Nothing changed. Try again."),
		});
	};
	return { patch, settle, failed };
}

/** Take over: the agent stops answering and the conversation is mine. */
function useTakeOver(detail: ConversationDetail) {
	const { patch, settle, failed } = useHandoffCache(detail);
	const { user } = useSession();
	return useMutation(
		orpc.conversations.takeOver.mutationOptions({
			onMutate: () =>
				patch({
					agentPausedAt: detail.agentPausedAt ?? new Date(),
					assignee: user
						? {
								id: user.id,
								name: user.name,
								image: user.image ?? null,
							}
						: detail.assignee,
				}),
			onSuccess: () =>
				toast.add({
					type: "success",
					title: "You took over",
					description:
						"The agent stops answering this shopper until you hand it back.",
				}),
			onError: failed("Couldn't take the conversation over"),
			onSettled: settle,
		}),
	);
}

/** Why a person cannot take this conversation over, or null when they can. */
function takeOverBlocked(detail: ConversationDetail): string | null {
	return detail.reply.reason === "no_channel"
		? "Nobody can write to the shopper on this channel."
		: null;
}

/** The header's Take over button, for conversations nobody asked a person to look at. */
export function TakeOverButton({ detail }: { detail: ConversationDetail }) {
	const takeOver = useTakeOver(detail);
	if (detail.agentPausedAt || detail.handoffRequestedAt) {
		return null;
	}
	const blocked = takeOverBlocked(detail);
	return (
		<DisabledReason reason={blocked}>
			<Button
				size="sm"
				variant="outline"
				disabled={Boolean(blocked) || takeOver.isPending}
				onClick={() => takeOver.mutate({ conversationId: detail.id })}
				// Icon only on phones, where the header is short of room.
				className="max-sm:w-7 max-sm:px-0"
			>
				{takeOver.isPending ? (
					<Spinner data-icon="inline-start" />
				) : (
					<HeadsetIcon data-icon="inline-start" />
				)}
				<span className="max-sm:sr-only">Take over</span>
			</Button>
		</DisabledReason>
	);
}

/** Who owns the conversation; any member can change it. */
export function AssigneeMenu({ detail }: { detail: ConversationDetail }) {
	const { settle, failed } = useHandoffCache(detail);
	const assign = useMutation(
		orpc.conversations.assign.mutationOptions({
			onSuccess: (_data, { assigneeId }) =>
				toast.add({
					type: "success",
					title: assigneeId ? "Conversation assigned" : "Unassigned",
				}),
			onError: failed("Couldn't change who owns this conversation"),
			onSettled: settle,
		}),
	);
	const { activeOrganization } = useActiveOrganization();
	const members = (activeOrganization?.members ?? []).map((m) => ({
		id: m.userId,
		name: m.user.name,
		image: m.user.image ?? null,
	}));
	const assignee = detail.assignee;
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<Button
						variant="ghost"
						size="sm"
						aria-label={
							assignee
								? `Assigned to ${assignee.name}. Change`
								: "Assign this conversation"
						}
						className="gap-1.5 px-1.5 text-muted-foreground"
					/>
				}
			>
				{assign.isPending ? (
					<Spinner />
				) : assignee ? (
					<UserAvatar
						size="sm"
						name={assignee.name}
						avatarUrl={assignee.image}
						className="size-5"
					/>
				) : (
					<UserRoundIcon />
				)}
				<span className="max-w-24 truncate max-sm:hidden">
					{assignee ? firstName(assignee.name) : "Unassigned"}
				</span>
				<ChevronDownIcon className="size-3.5" aria-hidden />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuGroup>
					<DropdownMenuLabel>Assign to</DropdownMenuLabel>
					<DropdownMenuRadioGroup
						value={assignee?.id ?? ""}
						onValueChange={(value: string) =>
							assign.mutate({
								conversationId: detail.id,
								assigneeId: value || null,
							})
						}
					>
						<DropdownMenuRadioItem value="">
							<UserRoundIcon aria-hidden />
							Nobody
						</DropdownMenuRadioItem>
						{members.map((member) => (
							<DropdownMenuRadioItem
								key={member.id}
								value={member.id}
							>
								<UserAvatar
									size="sm"
									name={member.name}
									avatarUrl={member.image}
									className="size-5"
								/>
								<span className="truncate">{member.name}</span>
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/**
 * The bar above the transcript while a person is needed or in charge: what is going on, and
 * the one action that moves it on.
 */
export function HandoffBar({ detail }: { detail: ConversationDetail }) {
	const { patch, settle, failed } = useHandoffCache(detail);
	const { user } = useSession();
	const me = user?.id ?? null;
	const takeOver = useTakeOver(detail);
	const handBack = useMutation(
		orpc.conversations.handBack.mutationOptions({
			onMutate: () =>
				patch({ agentPausedAt: null, handoffRequestedAt: null }),
			onSuccess: () =>
				toast.add({
					type: "success",
					title: "Handed back to the agent",
					description: "It answers the shopper's next message.",
				}),
			onError: failed("Couldn't hand the conversation back"),
			onSettled: settle,
		}),
	);
	const { confirm } = useConfirmationAlert();
	const escalated = detail.status === "ESCALATED";

	if (detail.agentPausedAt) {
		const holder = detail.assignee;
		const who =
			holder?.id === me
				? "You're handling this"
				: holder
					? `${firstName(holder.name)} is handling this`
					: "Your team is handling this";
		return (
			<Bar tone="info" icon={UserRoundIcon}>
				<p className="flex min-w-0 flex-1 items-center gap-1.5 font-medium">
					<span className="truncate">{who}</span>
					<InfoTip label="About the pause">
						The agent is paused: the shopper's messages arrive here
						and nobody else answers them. Hand back to let the agent
						reply again.
					</InfoTip>
				</p>
				<Button
					size="sm"
					variant="outline"
					disabled={handBack.isPending}
					onClick={() =>
						confirm({
							title: "Hand back to the agent?",
							message:
								"The agent answers this shopper's next message, and the shopper's page stops showing that a person is joining.",
							confirmLabel: "Hand back",
							// The mutation's onError already toasts.
							onConfirm: async () => {
								await handBack
									.mutateAsync({ conversationId: detail.id })
									.catch(() => undefined);
							},
						})
					}
				>
					{handBack.isPending ? (
						<Spinner data-icon="inline-start" />
					) : (
						<BotIcon data-icon="inline-start" />
					)}
					Hand back
				</Button>
			</Bar>
		);
	}

	if (!detail.handoffRequestedAt && !escalated) {
		return null;
	}
	const blocked = takeOverBlocked(detail);
	return (
		<Bar tone="warning" icon={HeadsetIcon}>
			<div className="flex min-w-0 flex-1 flex-col">
				<p className="font-medium">Needs a person</p>
				{detail.handoffReason ? (
					<p
						dir="auto"
						className="truncate text-muted-foreground text-xs"
						title={detail.handoffReason}
					>
						{detail.handoffReason}
					</p>
				) : null}
			</div>
			<DisabledReason reason={blocked}>
				<Button
					size="sm"
					disabled={Boolean(blocked) || takeOver.isPending}
					onClick={() =>
						takeOver.mutate({ conversationId: detail.id })
					}
				>
					{takeOver.isPending ? (
						<Spinner data-icon="inline-start" />
					) : (
						<HeadsetIcon data-icon="inline-start" />
					)}
					Take over
				</Button>
			</DisabledReason>
		</Bar>
	);
}

function Bar({
	tone,
	icon: Icon,
	children,
}: {
	tone: "info" | "warning";
	icon: typeof HeadsetIcon;
	children: React.ReactNode;
}) {
	return (
		<div
			role="status"
			className={cn(
				"flex shrink-0 items-center gap-2.5 border-b px-4 py-2 text-sm md:px-5",
				tone === "warning"
					? "border-warning/30 bg-warning/12 dark:bg-warning/8"
					: "border-info/25 bg-info/8 dark:bg-info/10",
			)}
		>
			<Icon
				className={cn(
					"size-4 shrink-0",
					tone === "warning"
						? "text-warning-foreground dark:text-warning"
						: "text-info",
				)}
				aria-hidden
			/>
			{children}
		</div>
	);
}

/**
 * Writing to the shopper while the agent is paused. The message goes out on the
 * conversation's own channel (the hosted page, the retailer's app, WhatsApp); WhatsApp only
 * takes free-form replies within 24 hours of the shopper's last message.
 */
export function StaffComposer({ detail }: { detail: ConversationDetail }) {
	const id = useId();
	const queryClient = useQueryClient();
	const [text, setText] = useState("");
	const send = useMutation(
		orpc.conversations.sendMessage.mutationOptions({
			// The transcript refetches (here and in every open inbox) from the live event.
			onSuccess: (sent) => {
				setText("");
				if (sent.delivery === "unknown") {
					toast.add({
						type: "warning",
						title: "Delivery not confirmed",
						description:
							"WhatsApp didn't answer in time, so the message may have reached the shopper. Check before sending it again.",
					});
				}
			},
			onError: (error) =>
				toast.add({
					type: "error",
					title: "Message not sent",
					description: errorMessage(error, "Try again in a moment."),
				}),
			onSettled: () =>
				queryClient.invalidateQueries(
					{ queryKey: orpc.conversations.key() },
					{ cancelRefetch: false },
				),
		}),
	);

	if (!detail.agentPausedAt || detail.reply.reason === "no_channel") {
		return null;
	}
	if (detail.reply.reason === "window_closed") {
		return (
			<div className="flex shrink-0 flex-col items-start gap-2 border-border/70 border-t px-4 py-3 text-sm sm:flex-row sm:items-center md:px-5">
				<p className="mx-auto max-w-3xl flex-1 text-muted-foreground">
					WhatsApp only allows replies within 24 hours of the
					shopper's last message
					{detail.reply.windowEndsAt ? (
						<>
							{" "}
							(closed{" "}
							<Time
								at={new Date(detail.reply.windowEndsAt)}
								as="relative"
								label="Window closed"
							/>
							)
						</>
					) : null}
					. You can write again when they do, or send an approved
					template now.
				</p>
				<SendTemplateButton detail={detail} />
			</div>
		);
	}

	const trimmed = text.trim();
	const tooLong = trimmed.length > STAFF_MESSAGE_MAX;
	const submit = () => {
		if (trimmed && !tooLong && !send.isPending) {
			send.mutate({ conversationId: detail.id, text: trimmed });
		}
	};

	return (
		<form
			className="shrink-0 border-border/70 border-t px-3 pt-2.5 pb-3 md:px-5"
			onSubmit={(event) => {
				event.preventDefault();
				submit();
			}}
		>
			<div className="mx-auto flex max-w-3xl flex-col gap-1.5">
				<label htmlFor={id} className="sr-only">
					Message to the shopper
				</label>
				<div className="flex items-end gap-2">
					{detail.channel === "WHATSAPP" ? (
						<SendMenu detail={detail} />
					) : null}
					<Textarea
						id={id}
						dir="auto"
						value={text}
						onChange={(event) => setText(event.target.value)}
						onKeyDown={(event) => {
							if (
								event.key === "Enter" &&
								!event.shiftKey &&
								!event.nativeEvent.isComposing
							) {
								event.preventDefault();
								submit();
							}
						}}
						placeholder="Write to the shopper"
						rows={1}
						className="max-h-40 min-h-10"
						aria-invalid={tooLong || undefined}
						// Read-only, not disabled: disabling blurs the field, so every Enter would
						// drop focus and the next message would be typed into nothing. The text
						// stays put until it is sent, so clearing it on success is still right.
						readOnly={send.isPending}
					/>
					<Tooltip>
						<TooltipTrigger
							render={
								<Button
									type="submit"
									size="icon"
									aria-label="Send to the shopper"
									disabled={
										!trimmed || tooLong || send.isPending
									}
								/>
							}
						>
							{send.isPending ? <Spinner /> : <SendIcon />}
						</TooltipTrigger>
						<TooltipContent className="flex items-center gap-1.5">
							Send <Kbd>Enter</Kbd>
							<span aria-hidden>·</span> New line{" "}
							<KbdGroup>
								<Kbd>Shift</Kbd>
								<Kbd>Enter</Kbd>
							</KbdGroup>
						</TooltipContent>
					</Tooltip>
				</div>
				{tooLong ? (
					<p className="text-destructive text-xs tabular">
						{trimmed.length.toLocaleString("en-US")} of{" "}
						{STAFF_MESSAGE_MAX.toLocaleString("en-US")} characters
					</p>
				) : detail.channel === "WHATSAPP" &&
					detail.reply.windowEndsAt ? (
					<p className="text-muted-foreground text-xs">
						WhatsApp takes replies until{" "}
						<Time
							at={new Date(detail.reply.windowEndsAt)}
							label="Window closes"
						/>
					</p>
				) : null}
			</div>
		</form>
	);
}
